import React, { useContext, useEffect, useState } from 'react';
import { useForm, FormProvider, UseFormSetError, FieldValues } from 'react-hook-form';

import useApi from 'common/useApi';
import useFormApiErrors from 'common/useFormApiErrors';
import AuthContext from 'common/auth';
import { isPlausibleBitcoinAddress } from 'common/isValidBitcoinAddress';

import DataTable from 'components/DataTable';
import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';

type PayoutAddress = {
    id: number,
    wallet_address: string
    is_primary: boolean
  };

const PayoutAddresses = () => {
  const {
    data,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
    refetch,
  } = useApi('getList', '/api/payout-addresses');
  const { isLoading, isFetching, isError, isSuccess, error, mutate } = useApi('patch', '/api/payout-addresses');
  const { mutate: mutatePost } = useApi('post', '/api/payout-addresses');
  const { user } = useContext(AuthContext)!;
  const [addressToEdit, setAddressToEdit] = useState<number | null>(null);

  const headers = [
    'Wallet Address',
    'Status',
    'Actions',
  ];

  const addressList = () => data?.map(({ id, wallet_address, is_primary }: PayoutAddress) => [
    wallet_address,
    {
      entry: is_primary ? 'Active' : 'Inactive',
      formatter: 'StatusBadge',
    },
    {
      entry: id,
      formatter: 'PayoutAddressButtons',
      action: editAddress,
    },
  ]);

  const editAddress = (id: number) => {
    setAddressToEdit(id);
    const addressObj = data?.find((address: PayoutAddress) => address.id === id);

    if (addressObj) {
      setValue('wallet_address', addressObj.wallet_address);
      setValue('is_primary', addressObj.is_primary);
    } else {
      setValue('wallet_address', '');
      setValue('is_primary', true);
    }
    setValue('token', '');

  };

  const formMethods = useForm({
    defaultValues: {
      wallet_address: '',
      is_primary: true,
      token: '',
    },
    mode: 'onChange',
  });

  const { handleSubmit, register, formState: { isSubmitting, isValid, isDirty }, setError, setValue } = formMethods;

  const formDisabled = isSubmitting || isLoading || isFetching || (!isValid && isDirty);

  const onSubmit = ({
    wallet_address,
    is_primary,
    token,
  }: {wallet_address: string, is_primary: boolean, token: string}) => {
    if (addressToEdit) {
      mutate({ id: addressToEdit, wallet_address, is_primary, token });
    } else {
      mutatePost({ wallet_address, is_primary, token });
    }
  };

  useFormApiErrors(
    isError,
    error,
    setError as unknown as UseFormSetError<FieldValues>,
    isSuccess,
    'Payout Settings updated successfully',
  );

  useEffect(() => {
    if(isSuccess) {
      refetch();
      setAddressToEdit(null);
    }
  }, [isSuccess]);


  return (
    <>
      {(isLoading || isFetching || isSubmitting) && <Loading />}
      <DataTable
        isLoading={isLoadingAddresses || isFetchingAddresses}
        headers={headers}
        content={addressList() ?? []}
      />
      <button onClick={() => editAddress(0)} className="button big primary">Add New Address</button>

      {addressToEdit !== null && <div className="box">
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel field='address' name='Your payout wallet address' className='half'>
              <input
                type='text'
                placeholder='Wallet Address'
                {...register('wallet_address', {
                  required: "Address can't be blank",
                  // Cheap upper bound (fits bech32m/taproot up to 62 chars).
                  maxLength: {
                    message: 'Address must not be more than 90 characters',
                    value: 90,
                  },
                  // Full bech32/bech32m checksum + structural base58 check. The
                  // server (OTP-gated) remains authoritative for the exact
                  // network and the base58 double-SHA256 checksum.
                  validate: (value) =>
                    isPlausibleBitcoinAddress(value) || 'Enter a valid Bitcoin address',
                })}
                disabled={!user?.has_device}
              />
            </FormLabel>
            <FormLabel field='is_primary' name='Active? (Only one active address allowed)' className='half'>
              <input
                type='checkbox'
                placeholder='Active?'
                {...register('is_primary')}
                disabled={!user?.has_device}
              />
            </FormLabel>
            <FormLabel field='token' name='Enter the one-time password generated by your OTP app' className='half'>
              <input
                type='text'
                placeholder='######'
                {...register('token', {
                  minLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  maxLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  required: 'Token can\'t be blank',
                })}
                disabled={!user?.has_device}
              />
            </FormLabel>
            <button
              type='submit'
              className='primary big'
              disabled={!user?.has_device || formDisabled}
            >
              Update Wallet Address
            </button>
            <button onClick={() => setAddressToEdit(null)} className="button big secondary">Cancel</button>
          </form>
        </FormProvider>
      </div>}
    </>
  );
};

export default PayoutAddresses;
